<template>
  <div class="max-w-4xl mx-auto text-center space-y-6 md:space-y-12">
    <div :class="['p-6 md:p-10 border-4 transition-all duration-500 relative', isEasy ? 'bg-amber-50/95 border-amber-800/50 sepia-shadow' : 'card-dark-rich border-amber-600/60 shadow-2xl shadow-black/40']" style="border-radius: 8px;">
      
      <!-- How to Play Trigger -->
      <button 
        @click="showHowToPlay = true"
        :class="['absolute top-2 right-2 md:top-4 md:right-4 px-3 py-1 md:px-4 md:py-2 border-2 font-serif-body font-bold text-xs md:text-sm transition-all hover:scale-105 hand-drawn-shadow', isEasy ? 'bg-amber-100 border-amber-800 text-amber-900' : 'bg-stone-800 border-amber-600 text-amber-200']"
        style="border-radius: 20px;"
      >
        How to Play?
      </button>

      <div class="mb-4 md:mb-8 flex flex-col items-center">
        <Mascot :size="isEasy ? 'w-36 h-36 md:w-64 md:h-64' : 'w-24 h-24 md:w-48 md:h-48'" :mood="selectedMode === 'easy' ? 'happy' : 'neutral'" :message="isEasy ? 'Welcome, little Nitwit!' : ''" />
        <h2 :class="['text-4xl md:text-6xl font-serif-display mb-2 text-shadow-warm transition-colors', isEasy ? 'ink-text' : 'text-amber-100']" style="letter-spacing: 0.02em;">
          Nitwit <span :class="isEasy ? 'text-amber-800' : 'text-amber-400'">Blitz</span>
        </h2>
        <p :class="['text-lg md:text-2xl font-serif-body mb-4 md:mb-6 leading-relaxed transition-colors', isEasy ? 'text-stone-700' : 'text-stone-300']">
          The ultimate timed riddle challenge! <br/>
          <span :class="['italic', isEasy ? 'text-stone-600' : 'text-stone-400']">How many can you solve before the clock runs out?</span>
        </p>
      </div>
      
      <div class="flex flex-wrap justify-center gap-2 md:gap-3 text-left mb-6 md:mb-8 max-w-lg mx-auto px-2">
        <div :class="['inline-flex items-center space-x-1.5 md:space-x-2 px-2.5 py-1.5 md:px-3 md:py-2 border-2 cursor-default', isEasy ? 'bg-green-50/90 border-green-700/60' : 'bg-green-900/30 border-green-700/50']" style="border-radius: 12px; transform: rotate(-0.5deg); box-shadow: 2px 2px 0 rgba(0,0,0,0.15);">
          <span class="text-base md:text-xl" style="color: #3d7c3d;">✓</span>
          <span :class="['font-serif-body font-bold text-[11px] md:text-sm whitespace-nowrap', isEasy ? 'text-green-900' : 'text-green-200']">+{{ GAME_CONFIG.CORRECT_ANSWER_BONUS }}s Correct</span>
        </div>
        <div :class="['inline-flex items-center space-x-1.5 md:space-x-2 px-2.5 py-1.5 md:px-3 md:py-2 border-2 cursor-default', isEasy ? 'bg-red-50/90 border-red-700/60' : 'bg-red-900/30 border-red-700/50']" style="border-radius: 12px; transform: rotate(0.5deg); box-shadow: 2px 2px 0 rgba(0,0,0,0.15);">
          <span class="text-base md:text-xl" style="color: #9d4b4b;">✗</span>
          <span :class="['font-serif-body font-bold text-[11px] md:text-sm whitespace-nowrap', isEasy ? 'text-red-900' : 'text-red-200']">3 Attempts</span>
        </div>
        <div :class="['inline-flex items-center space-x-1.5 md:space-x-2 px-2.5 py-1.5 md:px-3 md:py-2 border-2 cursor-default', isEasy ? 'bg-blue-50/90 border-blue-700/60' : 'bg-blue-900/30 border-blue-700/50']" style="border-radius: 12px; transform: rotate(-0.8deg); box-shadow: 2px 2px 0 rgba(0,0,0,0.15);">
          <span class="text-base md:text-xl" style="color: #5b7c9d;">🎟</span>
          <span :class="['font-serif-body font-bold text-[11px] md:text-sm whitespace-nowrap', isEasy ? 'text-blue-900' : 'text-blue-200']">{{ GAME_CONFIG.MAX_PASSES }} Passes</span>
        </div>
        <div :class="['inline-flex items-center space-x-1.5 md:space-x-2 px-2.5 py-1.5 md:px-3 md:py-2 border-2 cursor-default', isEasy ? 'bg-amber-50/90 border-amber-700/60' : 'bg-amber-900/30 border-amber-700/50']" style="border-radius: 12px; transform: rotate(0.8deg); box-shadow: 2px 2px 0 rgba(0,0,0,0.15);">
          <span class="text-base md:text-xl" style="color: #c9a961;">★</span>
          <span :class="['font-serif-body font-bold text-[11px] md:text-sm whitespace-nowrap', isEasy ? 'text-amber-900' : 'text-amber-200']">Global Ranks</span>
        </div>
      </div>

      <div class="mb-8 md:mb-10 max-w-md mx-auto space-y-4 md:space-y-6">
        <div>
          <label :class="['block text-xs md:text-sm font-serif-body font-bold mb-2 md:mb-3', isEasy ? 'text-amber-900' : 'text-amber-300']" style="letter-spacing: 0.05em;">Your Nitwit Name</label>
          <input
            id="player-nickname"
            name="nitwit-id"
            v-model="playerName"
            type="text"
            placeholder="WhoYouAre"
            autocomplete="off"
            data-lpignore="true"
            data-form-type="other"
            :disabled="isAuthenticated"
            :class="['w-full text-xl md:text-2xl font-serif-body p-4 md:p-5 transition-all text-center outline-none border-3 hand-drawn-shadow', isEasy ? 'bg-white/90 border-amber-800/60 ink-text placeholder-stone-400 focus:border-amber-900' : 'bg-stone-800/90 border-amber-700/50 text-amber-50 placeholder-stone-500 focus:border-amber-600', isAuthenticated ? 'opacity-70 cursor-not-allowed' : '']"
            style="border-radius: 6px;"
            @keyup.enter="handleStart"
          />
          <!-- Name status indicator -->
          <div class="mt-2 text-xs font-serif-body min-h-[20px]">
            <span v-if="isAuthenticated"
                  :class="['flex items-center justify-center gap-1', isEasy ? 'text-green-700' : 'text-green-400']">
              <span class="inline-block w-2 h-2 bg-green-600 rounded-full"></span>
              Logged in
              <button @click="handleLogout" class="underline ml-2 hover:opacity-70">(logout)</button>
            </span>
            <span v-else-if="isCheckingName" :class="isEasy ? 'text-stone-500' : 'text-stone-400'">
              Checking name...
            </span>
            <span v-else-if="nameCheckResult?.is_claimed"
                  :class="['flex items-center justify-center gap-1', isEasy ? 'text-amber-700' : 'text-amber-400']">
              <span class="inline-block w-2 h-2 bg-amber-600 rounded-full"></span>
              This name is registered - you'll need to verify
            </span>
            <span v-else-if="nameCheckResult && !nameCheckResult.is_claimed"
                  :class="['flex items-center justify-center gap-1', isEasy ? 'text-green-700' : 'text-green-400']">
              <span class="inline-block w-2 h-2 bg-green-600 rounded-full"></span>
              Available for play
            </span>
          </div>
        </div>

        <div>
          <label :class="['block text-xs md:text-sm font-serif-body font-bold mb-2 md:mb-3', isEasy ? 'text-amber-900' : 'text-amber-300']" style="letter-spacing: 0.05em;">Select Challenge</label>
          <div class="grid grid-cols-2 gap-3 md:gap-4">
            <button
              @click="setMode('blitz')"
              :class="['p-3 md:p-4 border-3 transition-all font-serif-body font-bold text-base md:text-lg hand-drawn-shadow', selectedMode === 'blitz' ? 'bg-amber-700 border-amber-900 text-white' : (isEasy ? 'bg-white/80 border-amber-700/40 ink-text hover:border-amber-700 hover:bg-amber-100/90' : 'bg-stone-800/80 border-amber-700/40 text-stone-300 hover:border-amber-500 hover:bg-stone-700/90')]"
              style="border-radius: 6px; letter-spacing: 0.05em;"
            >
              Blitz Mode
            </button>
            <button
              @click="setMode('easy')"
              :class="['p-3 md:p-4 border-3 transition-all font-serif-body font-bold text-base md:text-lg hand-drawn-shadow', selectedMode === 'easy' ? 'bg-amber-700 border-amber-900 text-amber-50' : (isEasy ? 'bg-white/80 border-amber-700/40 ink-text hover:border-amber-700 hover:bg-amber-100/90' : 'bg-stone-800/80 border-amber-700/40 text-stone-300 hover:border-amber-500 hover:bg-stone-700/90')]"
              style="border-radius: 6px; letter-spacing: 0.05em;"
            >
              Easy Mode
            </button>
          </div>
          <p :class="['mt-3 md:mt-4 text-xs md:text-sm font-serif-body italic transition-colors', isEasy ? 'text-stone-600' : 'text-stone-400']">
            {{ selectedMode === 'easy' ? 'Perfect for young nitwits! Multiple choice answers.' : 'The classic high-pressure timed challenge!' }}
          </p>
        </div>
      </div>

      <button
        @click="handleStart"
        :class="['group relative inline-flex items-center justify-center px-8 py-4 md:px-12 md:py-5 font-serif-display font-bold transition-all duration-200 focus:outline-none text-xl md:text-2xl hand-drawn-shadow border-4', isEasy ? 'bg-amber-700 hover:bg-amber-600 border-amber-900 text-amber-50' : 'bg-amber-700 hover:bg-amber-600 border-amber-800 text-white']"
        style="border-radius: 8px; letter-spacing: 0.05em;"
      >
        {{ isEasy ? 'Let\'s Play!' : 'Start the Blitz' }}
      </button>
    </div>

    <div :class="['p-6 md:p-10 border-4 transition-all duration-500', isEasy ? 'bg-amber-50/95 border-amber-800/50 sepia-shadow' : 'card-dark-rich border-amber-600/60 shadow-2xl shadow-black/40']" style="border-radius: 8px;">
      <h3 :class="['text-2xl md:text-3xl font-serif-display mb-6 md:mb-8 flex items-center justify-center space-x-3 text-shadow-warm', isEasy ? 'ink-text' : 'text-amber-100']">
        <span class="text-3xl md:text-4xl" style="color: #c9a961;">👑</span>
        <span>Top 10 {{ selectedMode === 'easy' ? 'Easy' : 'Blitz' }} Nitwits</span>
      </h3>
      <div v-if="loading" class="space-y-3 md:space-y-4">
        <div v-for="i in 5" :key="i" :class="['h-12 md:h-14 animate-pulse border-2', isEasy ? 'bg-amber-100/50 border-amber-800/30' : 'bg-slate-700/50 border-slate-600/30']" style="border-radius: 6px;"></div>
      </div>
      <div v-else class="space-y-2 md:space-y-3">
        <div v-for="(entry, index) in leaderboard" :key="entry.id" :class="['p-3 md:p-4 transition-colors group border-2', isEasy ? 'bg-amber-50/50 hover:bg-amber-100/70 border-amber-700/30' : 'bg-slate-700/30 hover:bg-slate-700/60 border-slate-500/30']" style="border-radius: 6px;">
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
              <span :class="['font-serif-body font-bold text-base md:text-lg flex-shrink-0', isEasy ? 'text-amber-800' : 'text-amber-400']">#{{ index + 1 }}</span>
              <span :class="['font-serif-body font-semibold text-sm md:text-base truncate flex-1', isEasy ? 'ink-text' : 'text-stone-200']">{{ entry.player_name }}</span>
            </div>
            <span :class="['px-2 py-1 md:px-3 md:py-1 font-serif-body font-bold text-base md:text-lg border-2 flex-shrink-0 cursor-default flex items-center gap-1', isEasy ? 'bg-amber-100/50 text-amber-800 border-amber-700/50' : 'bg-amber-900/20 text-amber-300 border-amber-700/50']" style="border-radius: 6px;">
              <span v-if="entry.perfect_game" class="text-amber-400" title="Perfect Game">&#x2B50;</span>
              <span v-else-if="entry.mastered_riddles" class="text-yellow-500" title="Riddle Master">&#x1F451;</span>
              {{ entry.final_score }}
            </span>
          </div>
          <div :class="['text-[10px] md:text-xs font-serif-body mt-2 text-right', isEasy ? 'text-stone-500' : 'text-stone-400']">
            {{ formatDate(entry.started_at) }}
          </div>
        </div>
      </div>
      <p v-if="!loading && leaderboard.length === 0" :class="['italic mt-4 font-serif-body', isEasy ? 'text-stone-500' : 'text-stone-400']">
        No nitwits yet. Will you be the first?
      </p>
    </div>

    <HowToPlay :is-open="showHowToPlay" :mode="selectedMode" @close="showHowToPlay = false" />

    <!-- Auth Modal -->
    <AuthModal
        :visible="showAuthModal"
        :is-easy="isEasy"
        :purpose="authPurpose"
        :player-name="playerName"
        @close="showAuthModal = false"
        @success="handleAuthSuccess"
    />
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue'
import apiClient from '../api'
import {GAME_CONFIG} from '../config'
import {authService} from '../services/authService'
import Mascot from './Mascot.vue'
import HowToPlay from './HowToPlay.vue'
import AuthModal from './AuthModal.vue'

const leaderboard = ref([])
const loading = ref(true)
const playerName = ref('')
const selectedMode = ref('blitz')
const showHowToPlay = ref(false)

// Auth state
const nameCheckResult = ref(null)
const showAuthModal = ref(false)
const authPurpose = ref('login')
const isCheckingName = ref(false)

const emit = defineEmits(['start-game', 'update-mode'])

const isEasy = computed(() => selectedMode.value === 'easy')
const isAuthenticated = computed(() => authService.isAuthenticated())

const setMode = (mode) => {
  selectedMode.value = mode
  emit('update-mode', mode)
}

const ADJECTIVES = ['Happy', 'Clever', 'Witty', 'Bright', 'Funny', 'Speedy', 'Silly', 'Brave', 'Kind', 'Cool']
const NOUNS = ['Panda', 'Owl', 'Fox', 'Dragon', 'Koala', 'Tiger', 'Dolphin', 'Robot', 'Wizard', 'Star']

const generateRandomUsername = () => {
  const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)]
  const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)]
  const num = Math.floor(Math.random() * 100)
  return `${adj}${noun}${num}`
}

// Debounced name check when user types
let nameCheckTimeout = null
watch(playerName, (newName) => {
  if (nameCheckTimeout) clearTimeout(nameCheckTimeout)
  nameCheckResult.value = null

  if (newName.trim().length >= 3 && !isAuthenticated.value) {
    isCheckingName.value = true
    nameCheckTimeout = setTimeout(async () => {
      try {
        nameCheckResult.value = await authService.checkName(newName.trim())
      } catch (error) {
        console.error('Name check failed:', error)
      } finally {
        isCheckingName.value = false
      }
    }, 500)
  }
})

const handleStart = () => {
  if (!playerName.value.trim()) {
    playerName.value = generateRandomUsername()
  }

  const name = playerName.value.trim()

  // Check if name is claimed and user is not authenticated
  if (nameCheckResult.value?.is_claimed && !isAuthenticated.value) {
    // Name is claimed - show login modal
    authPurpose.value = 'login'
    showAuthModal.value = true
    return
  }

  localStorage.setItem('nitwit_username', name)
  emit('start-game', name, selectedMode.value)
}

// Handle successful auth from modal
const handleAuthSuccess = (result) => {
  showAuthModal.value = false
  const player = authService.getPlayer()
  if (player) {
    playerName.value = player.player_name
    nameCheckResult.value = null
  }
  // Now start the game
  localStorage.setItem('nitwit_username', playerName.value.trim())
  emit('start-game', playerName.value.trim(), selectedMode.value)
}

// Handle logout
const handleLogout = async () => {
  await authService.logout()
  nameCheckResult.value = null
}

const fetchLeaderboard = async () => {
  loading.value = true
  try {
    const response = await apiClient.get(`/game/leaderboard/?mode=${selectedMode.value}`)
    leaderboard.value = response.data
  } catch (error) {
    console.error('Failed to fetch leaderboard:', error)
  } finally {
    loading.value = false
  }
}

watch(selectedMode, () => {
  fetchLeaderboard()
})

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString()
}

onMounted(() => {
  fetchLeaderboard()
  // Check if already authenticated
  const player = authService.getPlayer()
  if (player) {
    playerName.value = player.player_name
  } else {
    const savedUsername = localStorage.getItem('nitwit_username')
    if (savedUsername) {
      playerName.value = savedUsername
    } else {
      playerName.value = generateRandomUsername()
    }
  }
})
</script>
